import React, { useState, useContext } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { Truck, Radio, Fuel, TrendingUp } from "lucide-react";
import { AuthContext } from "../context/AuthContext";

const FEATURES = [
  { icon: Radio, text: "Live fleet tracking, HOS, and fuel state" },
  { icon: TrendingUp, text: "Real negotiation floors on every broker call" },
  { icon: Fuel, text: "Fuel and rest-stop planning built into every route" },
];

// Everything reachable by typing/bookmarking a URL (bare "/", "/login"
// itself) falls through to this component -- see App.js's catch-all "*"
// route -- so this is the one place that needs to notice an existing,
// still-valid session and skip the form entirely instead of making an
// already-logged-in dispatcher/admin re-enter their password.
const ALREADY_LOGGED_IN_HOME_ROLES = ["dispatcher", "admin", "super_admin"];

const Login = () => {
  const { login, user, isAuthLoading } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = new URLSearchParams(location.search).get("redirect");

  // AuthContext's own useEffect (reading localStorage) hasn't resolved yet
  // on first paint -- rendering nothing for this one tick avoids a flash of
  // the login form for a user who turns out to already be signed in.
  if (isAuthLoading) return null;

  if (user?.token) {
    // Same forced-reset gate PrivateRoute.js enforces on every other
    // protected route -- an already-logged-in session that still owes a
    // password reset goes there, not straight past it to /dispatch.
    if (user.mustResetPassword) return <Navigate to="/change-password" replace />;
    if (redirectPath) return <Navigate to={redirectPath} replace />;
    if (user.roles?.some((r) => ALREADY_LOGGED_IN_HOME_ROLES.includes(r))) {
      return <Navigate to="/dispatch" replace />;
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const result = await login(email, password);

      if (!result.success) {
        setError("Invalid email or password.");
        return;
      }

      // A specific deep link the user was trying to reach before being
      // bounced to /login wins over any default redirect rule.
      if (redirectPath?.startsWith("/interview/") || redirectPath?.startsWith("/portal/")) {
        navigate(redirectPath);
        return;
      }

      // Otherwise, defer to whatever login_redirect_rules resolved server-
      // side (APIs/acl.py::resolve_login_redirect) based on this user's
      // role/group/department -- falls back to the default dashboard if no
      // rule matched.
      navigate(result.redirectPath || "/dashboard");
    } catch (err) {
      console.error("❌ Login error:", err);
      setError("Invalid email or password.");
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-white">
      {/* Brand panel -- hidden below lg, this is where the app's own
          identity (dark navy + blue, same as DispatcherLayout's sidebar)
          actually lives. The login form itself stays plain/functional; all
          the "fancy" is spent here, not on the inputs. */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0b1220] px-14 py-12 lg:flex">
        <div
          className="pointer-events-none absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #22a8e0 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 right-[-120px] h-[420px] w-[420px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #1d6fd8 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />

        <div className="relative z-10 flex items-center gap-2.5">
          <img
            src="/logo-mark.png"
            alt="AIVAN"
            className="h-9 w-9 flex-shrink-0 rounded-[10px] object-cover shadow-[0_2px_10px_rgba(29,111,216,0.45)]"
          />
          <span className="text-sm font-extrabold tracking-wide text-slate-100">AIVAN</span>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-sky-300">
            <Truck size={12} /> Decision layer for your fleet
          </div>
          <h1 className="text-3xl font-bold leading-tight text-white">
            Run the fleet on real numbers, not gut feel.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-400">
            Cost, profit, and HOS math on every truck and every load --
            computed live, not estimated after the fact.
          </p>

          <div className="mt-9 space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/5 text-sky-300 ring-1 ring-white/10">
                  <Icon size={14} />
                </div>
                <span className="text-sm text-slate-300">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500">Built for dispatchers who live in the details.</div>
      </div>

      {/* Form panel */}
      <div className="flex w-full flex-1 items-center justify-center bg-[#f7faf9] px-4 py-12 lg:w-1/2 lg:bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile-only mark -- the brand panel above is hidden below lg,
              so this is the only place small screens see it. */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <img
              src="/logo-mark.png"
              alt="AIVAN"
              className="h-9 w-9 flex-shrink-0 rounded-[10px] object-cover shadow-[0_2px_10px_rgba(29,111,216,0.35)]"
            />
            <span className="text-sm font-extrabold tracking-wide text-[#0b1220]">AIVAN</span>
          </div>

          <h2 className="text-2xl font-bold text-[#0b1220]">Sign in</h2>
          <p className="mt-1 mb-8 text-sm text-slate-500">Welcome back -- enter your details to continue.</p>

          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600">
              <svg className="h-4 w-4 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10A8 8 0 11.999 10 8 8 0 0118 10zm-8.707-3.707a1 1 0 011.414 0L11 7.586V11a1 1 0 11-2 0V7.586l.293-.293zM11 13a1 1 0 10-2 0 1 1 0 002 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-[#1d6fd8] focus:ring-2 focus:ring-[#1d6fd8]/20"
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-[#1d6fd8] hover:text-[#12459e]">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-[#1d6fd8] focus:ring-2 focus:ring-[#1d6fd8]/20"
              />
            </div>

            <button
              type="submit"
              className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(29, 111, 216,0.35)] transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#1d6fd8]/40"
              style={{ background: "linear-gradient(135deg, #22a8e0, #1d6fd8)" }}
            >
              Sign in
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            Trouble signing in? Contact your fleet admin.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
